import React, { useState, useEffect, useCallback } from 'react';
import { marketsApi, packingAssignmentsApi } from '../../services/api';
import AdminPageHeader from '../../components/AdminPageHeader';
import './PackingAssignments.css';

export default function PackingAssignments() {
  const [markets, setMarkets] = useState([]);
  const [assignments, setAssignments] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState('');
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [mData, aData] = await Promise.all([
        marketsApi.getAll(),
        packingAssignmentsApi.getAll(),
      ]);
      const activeMarkets = (mData.markets || []).filter(m => m.is_active);
      setMarkets(activeMarkets);
      const map = {};
      (aData.assignments || []).forEach(a => {
        map[a.saturday_market_id] = a.sunday_market_id;
      });
      setAssignments(map);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function handleChange(saturdayMarketId, sundayMarketId) {
    setSaving(saturdayMarketId);
    setAssignments(a => ({ ...a, [saturdayMarketId]: sundayMarketId }));
    try {
      await packingAssignmentsApi.set(saturdayMarketId, sundayMarketId);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving('');
    }
  }

  const saturdayMarkets = markets.filter(m => m.day_of_week === 'Saturday');
  const sundayMarkets = markets.filter(m => m.day_of_week === 'Sunday');

  return (
    <div className="page">
      <AdminPageHeader
        title="Packing Assignments"
        subtitle="Which Sunday market each Saturday team packs for"
      />
      <div className="page-body">
        {error && <div className="error-banner">{error}</div>}

        {loading ? (
          <div className="loading-state">Loading...</div>
        ) : (
          <div className="markets-table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Saturday Market</th>
                  <th>Packs For (Sunday)</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {saturdayMarkets.map(market => {
                  const assigned = assignments[market.id];
                  const isSaving = saving === market.id;
                  return (
                    <tr key={market.id}>
                      <td className="td-name">{market.name}</td>
                      <td>
                        <select
                          className="assignment-select"
                          value={assigned || 'none'}
                          onChange={e => handleChange(market.id, e.target.value)}
                          disabled={isSaving}
                        >
                          <option value="none">— None —</option>
                          {sundayMarkets.map(sm => (
                            <option key={sm.id} value={sm.id}>{sm.name}</option>
                          ))}
                        </select>
                      </td>
                      <td>
                        {isSaving ? (
                          <span className="saving-text">Saving...</span>
                        ) : assigned && assigned !== 'none' ? (
                          <span className="pill pill-green">Assigned</span>
                        ) : (
                          <span className="pill pill-orange">Unassigned</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <div className="assignment-note">
          <p>These assignments are used to generate the individual Saturday → Sunday pack sheets. Changes take effect immediately.</p>
        </div>
      </div>
    </div>
  );
}
